import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';

import { isConcludedStatus, isSignedStatus } from './Documents.utils';
import type {
	DocumentProgress,
	DocumentSignerGroup,
	GroupProgress,
} from './documentSigningProgress.types';

const PARENT_MERGED_ROLE_TYPES = new Set([
	DocumentSigningRoleType.PIV,
	DocumentSigningRoleType.REPRESENTATIVE,
]);

export const getGroupProgress = (group: DocumentSignerGroup): GroupProgress => {
	const isCounterparty = group.groupType === DocumentSigningRoleType.COUNTERPARTY;
	const totalSlots = isCounterparty
		? group.requiredNumberOfSignatures ?? 0
		: group.signers.length;

	const signedCount = group.signers.filter((s) => isSignedStatus(s.status)).length;

	const filledCount = group.signers.filter((s) => s.assigned || isSignedStatus(s.status)).length;

	const missingAssignments = isCounterparty ? Math.max(0, totalSlots - filledCount) : 0;

	// Concluded, not signed: a slot closed without a signature (DC-1098) will never be
	// chased again, so it is not outstanding — but it is not signed either, so `signedCount`
	// above deliberately still excludes it.
	const unsignedAssigned = group.signers.filter(
		(s) => s.assigned && !isConcludedStatus(s.status)
	).length;

	const outstandingCount = missingAssignments + unsignedAssigned;

	return {
		isComplete: outstandingCount === 0,
		outstandingCount,
		missingAssignments,
		unsignedAssigned,
		totalSlots,
		signedCount,
	};
};

export const getDocumentProgress = (groups: DocumentSignerGroup[]): DocumentProgress => {
	let signed = 0;
	let total = 0;
	let signaturesOutstanding = 0;
	let signersNeedingAssignment = 0;

	for (const group of groups) {
		// Skip parent merged role types as their progress is derived from their child groups
		if (PARENT_MERGED_ROLE_TYPES.has(group.groupType)) {
			continue;
		}

		const progress = getGroupProgress(group);
		signed += progress.signedCount;
		total += progress.totalSlots;
		signaturesOutstanding += progress.outstandingCount;
		signersNeedingAssignment += progress.missingAssignments;
	}

	return {
		signed,
		total,
		signaturesOutstanding,
		signersNeedingAssignment,
		isComplete: signaturesOutstanding === 0 && signersNeedingAssignment === 0,
	};
};
